import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip,
  InputAdornment,
  ButtonGroup,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import UserPermissionCard from '@/components/groups/UserPermissionCard';
import { snakeToTitleCase, permissionTemplates, deriveRoleFromPermissions } from '@/components/groups/constants';

function UsersPermissionsPanel({
  users,
  formData,
  onUpdate,
  onUserSelection,
  onBatchRoleChange,
  onBatchPermissionChange,
}) {
  const [userSearch, setUserSearch] = React.useState('');
  const [batchRole, setBatchRole] = React.useState(null);
  const [batchPermissions, setBatchPermissions] = React.useState(() => {
    // Initialize with all permissions enabled by default
    const defaultPerms = {};
    Object.keys(permissionTemplates.trader || {}).forEach((key) => {
      defaultPerms[key] = true;
    });
    return defaultPerms;
  });

  React.useEffect(() => {
    // No pagination to reset
  }, [userSearch]);

  const normalizedQuery = userSearch.trim().toLowerCase();
  const filteredUsers = normalizedQuery
    ? users.filter(
        (u) => u.username.toLowerCase().includes(normalizedQuery) || u.email.toLowerCase().includes(normalizedQuery)
      )
    : users;

  // Use default ordering; do not move selected users to the top
  const displayedUsers = filteredUsers;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h6'>Add Users & Set Permissions</Typography>
        <Chip color='primary' label={`${formData.selectedUsers?.length} users selected`} variant='outlined' />
      </Box>

      <Box sx={{ display: 'flex', mb: 2 }}>
        <TextField
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          placeholder='Search users by name or email...'
          size='medium'
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
        />
      </Box>

      {formData.selectedUsers?.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography sx={{ mb: 2 }} variant='subtitle1'>
              Batch Actions
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', mb: 2 }}>
              <ButtonGroup aria-label='batch role actions'>
                <Button
                  color={batchRole === 'trader' ? 'primary' : 'inherit'}
                  size='small'
                  variant={batchRole === 'trader' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setBatchRole('trader');
                    onBatchRoleChange('trader');
                  }}
                >
                  Make All Traders
                </Button>
                <Button
                  color={batchRole === 'viewer' ? 'primary' : 'inherit'}
                  size='small'
                  variant={batchRole === 'viewer' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setBatchRole('viewer');
                    onBatchRoleChange('viewer');
                  }}
                >
                  Make All Viewers
                </Button>
                <Button
                  color={batchRole === 'isolated_trader' ? 'primary' : 'inherit'}
                  size='small'
                  variant={batchRole === 'isolated_trader' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setBatchRole('isolated_trader');
                    onBatchRoleChange('isolated_trader');
                  }}
                >
                  Make All Isolated Traders
                </Button>
              </ButtonGroup>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flex: 1, minWidth: 240 }}>
                {Object.keys(permissionTemplates.trader).map((permission) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={batchPermissions[permission] || false}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setBatchPermissions((prev) => ({ ...prev, [permission]: newValue }));
                          onBatchPermissionChange(permission, newValue);
                        }}
                      />
                    }
                    key={permission}
                    label={snakeToTitleCase(permission)}
                  />
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {displayedUsers.length === 0 ? (
          <Grid item xs={12}>
            <Typography color='text.secondary' variant='body2'>
              No users found.
            </Typography>
          </Grid>
        ) : (
          displayedUsers.map((user) => (
            <Grid item key={user.id} sm={6} xs={12}>
              <UserPermissionCard
                isSelected={formData.selectedUsers?.includes(user.id)}
                user={user}
                userPermissions={formData.userPermissions?.[user.id]}
                onChangeRole={(role) =>
                  onUpdate({
                    userPermissions: {
                      ...formData.userPermissions,
                      [user.id]: { role, permissions: { ...permissionTemplates[role] } },
                    },
                  })
                }
                onTogglePermission={(permission, enabled) => {
                  const current = formData.userPermissions?.[user.id]?.permissions || {};
                  const updatedPerms = { ...current, [permission]: enabled };
                  const updatedRole = deriveRoleFromPermissions(updatedPerms);
                  onUpdate({
                    userPermissions: {
                      ...formData.userPermissions,
                      [user.id]: {
                        role: updatedRole,
                        permissions: updatedPerms,
                      },
                    },
                  });
                }}
                onToggleSelected={(selected) => onUserSelection(user.id, selected)}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
}

export default UsersPermissionsPanel;
